import { Suspense } from "react";
import { Sparkles } from "@react-three/drei";
import Player from "./components/Player";
import {
  RoomShell, DNAChamber, ProductDisplay, HoloSign, ScientistAvatar, Console, Molecule, Safe,
} from "./components/primitives";
import { COLORS, ATRIUM_NAV, navTransforms, SCIENTIST_SPOTS } from "./world";

function GalleryProducts({ catalog, accent, onInspect, onHover }) {
  const per = 6, spacing = 3, startX = -((per - 1) * spacing) / 2;
  return catalog.map((p, i) => {
    const row = Math.floor(i / per), col = i % per;
    const x = startX + col * spacing;
    const z = 5 - row * 3;
    return (
      <Safe key={p.slug}>
        <ProductDisplay product={p} position={[x, 0, z]} accent={accent} onInspect={onInspect} onHover={onHover} />
      </Safe>
    );
  });
}

export default function Scene({ vr }) {
  const { room, setRoom, catalog, world, input, setInspect, setScientist, setShowCOA, setShowMolecule, setShowTimeline, track, onCalc, onCheckout } = vr;
  const roomMeta = (world.rooms || []).find((r) => r.key === room) || {};
  const accent = roomMeta.accent || COLORS.cyan;
  const reflect = vr.quality !== "low";

  const inspect = (slug) => { if (input.current.dragging) return; setInspect(slug); track("product_inspect", { product_slug: slug, room }); };
  const galleryCatalog = catalog;
  const catForRoom = (roomMeta.categories || []);
  const roomProducts = catalog.filter((p) => (p.categories || []).some((c) => catForRoom.includes(c)));

  return (
    <>
      <color attach="background" args={[COLORS.obsidian]} />
      <fog attach="fog" args={[COLORS.obsidian, 18, 46]} />
      <ambientLight intensity={0.35} />
      <hemisphereLight intensity={0.3} color={accent} groundColor="#02040a" />
      <pointLight position={[0, 10, 6]} intensity={1.2} color="#bfe9ff" />
      <pointLight position={[-12, 8, -8]} intensity={0.8} color={COLORS.violet} />
      <pointLight position={[12, 8, -8]} intensity={0.8} color={COLORS.cyan} />

      <Player input={input} />

      <RoomShell accent={accent} reflect={reflect} />
      {vr.quality !== "low" && <Sparkles count={40} scale={[30, 8, 30]} size={2} speed={0.3} color={accent} position={[0, 4, 0]} />}

      <Suspense fallback={null}>
        {room === "atrium" && (
          <>
            <DNAChamber />
            {ATRIUM_NAV.map((n, i) => {
              const t = navTransforms(ATRIUM_NAV.length)[i];
              return <HoloSign key={n.key} label={n.label} color={n.color} position={[t.x, 0, t.z]} angle={t.angle} onClick={() => setRoom(n.key)} />;
            })}
          </>
        )}

        {room === "gallery" && (
          <>
            <pointLight position={[-6, 6, 2]} intensity={1.6} color="#dff4ff" distance={22} />
            <pointLight position={[6, 6, 2]} intensity={1.6} color="#dff4ff" distance={22} />
            <pointLight position={[0, 6, -6]} intensity={1.4} color={accent} distance={22} />
            <GalleryProducts catalog={galleryCatalog} accent={accent} onInspect={inspect} onHover={() => {}} />
          </>
        )}

        {room === "research" && (
          <>
            <Molecule color={COLORS.violet} />
            <Console label="RESEARCH TIMELINE" color={COLORS.violet} position={[-3.8, 0, -3]} onClick={() => setShowTimeline(true)} />
            <Console label="ASK A SCIENTIST" color={COLORS.cyan} position={[3.8, 0, -3]} onClick={() => setScientist("nova")} />
          </>
        )}

        {["performance", "longevity", "metabolic", "cognition"].includes(room) && (
          <>
            <pointLight position={[-6, 6, 2]} intensity={1.4} color="#dff4ff" distance={22} />
            <pointLight position={[6, 6, 2]} intensity={1.4} color={accent} distance={22} />
            <group scale={1.1} position={[0, 2, -9]}><Molecule color={accent} /></group>
            <GalleryProducts catalog={roomProducts} accent={accent} onInspect={inspect} onHover={() => {}} />
          </>
        )}

        {room === "molecular" && (
          <>
            <pointLight position={[0, 6, 2]} intensity={1.6} color={accent} distance={24} />
            <group scale={1.4} position={[0, 0.6, -5]}><Molecule color={accent} /></group>
            <Console label="EXPLORE MOLECULES" color={accent} position={[0, 0, 5]} onClick={() => setShowMolecule(true)} />
          </>
        )}

        {room === "quality" && (
          <Console label="SCAN / VERIFY BATCH" color={COLORS.mint} position={[0, 0, -2]} onClick={() => setShowCOA(true)} />
        )}

        {room === "calculator" && (
          <Console label="PRECISION CALCULATOR" color={COLORS.cyan} position={[0, 0, -2]} onClick={() => onCalc && onCalc()} />
        )}

        {room === "scientists" && (
          (world.scientists || []).slice(0, 4).map((s, i) => (
            <ScientistAvatar key={s.key} sci={s} position={[SCIENTIST_SPOTS[i].x, 0, SCIENTIST_SPOTS[i].z]} onTalk={(k) => setScientist(k)} />
          ))
        )}

        {room === "checkout" && (
          <Console label="CHECKOUT TERMINAL" color={COLORS.mint} position={[0, 0, -2]} onClick={() => onCheckout && onCheckout()} />
        )}
      </Suspense>
    </>
  );
}
